import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About',
      contact: 'Contact',
      getQuote: 'Get a Quote',
      platform: 'SafeProG Platform'
    },
    // Hero
    hero: {
      tagline: 'Safety • Strategy • Technology • Compliance',
      headline: 'Enabling Safe, Compliant, and Smart Business Worldwide',
      subheadline: 'UAE-based, globally operating professional advisory firm delivering integrated Occupational Health & Safety, Project Management, Compliance, Payroll, and IT consulting services.',
      cta: 'Consult with Experts',
      learnMore: 'Learn More'
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive solutions to protect people, strengthen governance, and deliver projects efficiently',
      ehs: {
        title: 'EHS Consultancy',
        shortTitle: 'Health, Safety & Environment',
        description: 'Design, implement, and maintain practical EHS Management Systems aligned with ISO 45001 & ISO 14001.',
        features: [
          'ISO 45001 & ISO 14001 management systems',
          'EHS policies, procedures, SOPs',
          'Hazard identification & risk assessments',
          'Incident investigation & CAPA',
          'Internal audits & compliance reporting'
        ]
      },
      risk: {
        title: 'Risk Management & Compliance',
        shortTitle: 'Risk & Compliance',
        description: 'Identify, assess, and manage operational, project, and corporate risks using structured methodologies.',
        features: [
          'Enterprise risk management frameworks',
          'Risk workshops & assessments',
          'Control effectiveness reviews',
          'Governance & regulatory alignment',
          'Business continuity support'
        ]
      },
      pmo: {
        title: 'Project Management & PMO',
        shortTitle: 'Project Management',
        description: 'Structured PMO support aligned with recognized best practices for controlled delivery.',
        features: [
          'Project execution plans',
          'Planning, scheduling & controls',
          'Programme governance support',
          'Construction project oversight',
          'Risk-based reporting'
        ]
      },
      payroll: {
        title: 'Payroll & Tax Compliance',
        shortTitle: 'Payroll Services',
        description: 'Confidential payroll and tax services for Ireland and UK operations.',
        features: [
          'Payroll processing & compliance',
          'PAYE, PRSI, USC (Ireland)',
          'UK PAYE & National Insurance',
          'Personal & contractor tax returns',
          'Revenue & HMRC liaison'
        ]
      },
      it: {
        title: 'IT Consulting',
        shortTitle: 'IT Solutions',
        description: 'Development and assurance of secure, scalable digital solutions.',
        features: [
          'Application development support',
          'Software testing & QA',
          'Functional, regression & UAT testing',
          'Digital process optimization',
          'EHS & PM digital tools integration'
        ]
      },
      viewAll: 'View All Services',
      learnMore: 'Learn More'
    },
    // Industries
    industries: {
      title: 'Industries We Serve',
      subtitle: 'Delivering excellence across diverse sectors',
      list: ['Construction', 'Engineering', 'Energy', 'Manufacturing', 'Infrastructure', 'IT & Digital', 'Corporate Services', 'Data Centre Construction']
    },
    // About
    about: {
      title: 'About SafePro Global',
      subtitle: 'Your trusted partner in safety and compliance',
      mission: 'Our Mission',
      missionText: 'We are committed to protecting people, safeguarding the environment, strengthening governance, and enabling sustainable performance through structured systems, risk-based thinking, and technology-enabled solutions.',
      whyUs: 'Why SafePro?',
      reasons: [
        'Integrated EHS, PMO, compliance, payroll, and IT expertise',
        'Alignment with ISO 45001, ISO 14001, and recognized PMO frameworks',
        'International regulatory and operational experience',
        'Practical, implementation-focused delivery model',
        'Scalable support for SMEs to large programmes',
        'Confidential, ethical, and professional engagement'
      ],
      globalPresence: 'Global Presence',
      globalText: 'SafePro operates across Europe, the Middle East, and international markets, supporting clients throughout the full business and project lifecycle.'
    },
    // Testimonials
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Trusted by industry leaders worldwide'
    },
    // Contact
    contact: {
      title: 'Contact Us',
      subtitle: 'Ready to discuss your requirements?',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        company: 'Company Name',
        service: 'Service of Interest',
        message: 'Your Message',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Thank you! We will contact you shortly.',
        error: 'Something went wrong. Please try again.',
        selectService: 'Select a service'
      },
      info: {
        location: 'Location',
        locationValue: 'UAE | Operating Globally',
        email: 'Email',
        emailValue: 'info@safeproglobal.com',
        website: 'Website',
        websiteValue: 'www.safeproglobal.com'
      }
    },
    // Footer
    footer: {
      description: 'Enabling Safe, Compliant, and Smart Business Worldwide.',
      quickLinks: 'Quick Links',
      services: 'Services',
      contact: 'Contact Info',
      rights: '© 2025 SafePro Global Consultancy. All rights reserved.'
    },
    // CTA Section
    cta: {
      title: 'Ready to Transform Your Operations?',
      subtitle: 'Let our experts help you achieve compliance, safety, and operational excellence.',
      button: 'Get Started Today'
    }
  },
  ar: {
    // Navigation
    nav: {
      home: 'الرئيسية',
      services: 'الخدمات',
      about: 'من نحن',
      contact: 'اتصل بنا',
      getQuote: 'احصل على عرض سعر',
      platform: 'منصة SafeProG'
    },
    // Hero
    hero: {
      tagline: 'السلامة • الاستراتيجية • التكنولوجيا • الامتثال',
      headline: 'تمكين الأعمال الآمنة والمتوافقة والذكية في جميع أنحاء العالم',
      subheadline: 'شركة استشارية مهنية مقرها الإمارات وتعمل عالمياً، تقدم خدمات متكاملة في الصحة والسلامة المهنية وإدارة المشاريع والامتثال والرواتب واستشارات تكنولوجيا المعلومات.',
      cta: 'استشر الخبراء',
      learnMore: 'اعرف المزيد'
    },
    // Services
    services: {
      title: 'خدماتنا',
      subtitle: 'حلول شاملة لحماية الناس وتعزيز الحوكمة وتنفيذ المشاريع بكفاءة',
      ehs: {
        title: 'استشارات الصحة والسلامة والبيئة',
        shortTitle: 'الصحة والسلامة والبيئة',
        description: 'تصميم وتنفيذ وصيانة أنظمة إدارة الصحة والسلامة والبيئة المتوافقة مع ISO 45001 و ISO 14001.',
        features: [
          'أنظمة إدارة ISO 45001 و ISO 14001',
          'سياسات وإجراءات الصحة والسلامة والبيئة',
          'تحديد المخاطر وتقييمها',
          'التحقيق في الحوادث والإجراءات التصحيحية',
          'التدقيق الداخلي وتقارير الامتثال'
        ]
      },
      risk: {
        title: 'إدارة المخاطر والامتثال',
        shortTitle: 'المخاطر والامتثال',
        description: 'تحديد وتقييم وإدارة المخاطر التشغيلية والمشاريع باستخدام منهجيات منظمة.',
        features: [
          'أطر إدارة مخاطر المؤسسات',
          'ورش عمل وتقييمات المخاطر',
          'مراجعات فعالية الرقابة',
          'مواءمة الحوكمة والتنظيم',
          'دعم استمرارية الأعمال'
        ]
      },
      pmo: {
        title: 'إدارة المشاريع ومكتب إدارة المشاريع',
        shortTitle: 'إدارة المشاريع',
        description: 'دعم مكتب إدارة المشاريع المنظم المتوافق مع أفضل الممارسات المعترف بها.',
        features: [
          'خطط تنفيذ المشاريع',
          'التخطيط والجدولة والرقابة',
          'دعم حوكمة البرامج',
          'إشراف مشاريع البناء',
          'التقارير القائمة على المخاطر'
        ]
      },
      payroll: {
        title: 'الرواتب والامتثال الضريبي',
        shortTitle: 'خدمات الرواتب',
        description: 'خدمات رواتب وضرائب سرية لعمليات أيرلندا والمملكة المتحدة.',
        features: [
          'معالجة الرواتب والامتثال',
          'PAYE و PRSI و USC (أيرلندا)',
          'PAYE والتأمين الوطني في المملكة المتحدة',
          'إقرارات ضريبية شخصية وللمقاولين',
          'التواصل مع الإيرادات و HMRC'
        ]
      },
      it: {
        title: 'استشارات تكنولوجيا المعلومات',
        shortTitle: 'حلول تكنولوجيا المعلومات',
        description: 'تطوير وضمان حلول رقمية آمنة وقابلة للتطوير.',
        features: [
          'دعم تطوير التطبيقات',
          'اختبار البرمجيات وضمان الجودة',
          'اختبار وظيفي ورجعي و UAT',
          'تحسين العمليات الرقمية',
          'تكامل أدوات الصحة والسلامة وإدارة المشاريع الرقمية'
        ]
      },
      viewAll: 'عرض جميع الخدمات',
      learnMore: 'اعرف المزيد'
    },
    // Industries
    industries: {
      title: 'القطاعات التي نخدمها',
      subtitle: 'تقديم التميز عبر قطاعات متنوعة',
      list: ['البناء', 'الهندسة', 'الطاقة', 'التصنيع', 'البنية التحتية', 'تكنولوجيا المعلومات والرقمية', 'خدمات الشركات', 'بناء مراكز البيانات']
    },
    // About
    about: {
      title: 'عن SafePro Global',
      subtitle: 'شريكك الموثوق في السلامة والامتثال',
      mission: 'مهمتنا',
      missionText: 'نحن ملتزمون بحماية الناس والحفاظ على البيئة وتعزيز الحوكمة وتمكين الأداء المستدام من خلال الأنظمة المنظمة والتفكير القائم على المخاطر والحلول المدعومة بالتكنولوجيا.',
      whyUs: 'لماذا SafePro؟',
      reasons: [
        'خبرة متكاملة في الصحة والسلامة والبيئة وإدارة المشاريع والامتثال والرواتب وتكنولوجيا المعلومات',
        'التوافق مع ISO 45001 و ISO 14001 وأطر إدارة المشاريع المعترف بها',
        'خبرة تنظيمية وتشغيلية دولية',
        'نموذج تسليم عملي يركز على التنفيذ',
        'دعم قابل للتطوير للشركات الصغيرة والمتوسطة إلى البرامج الكبيرة',
        'التزام سري وأخلاقي ومهني'
      ],
      globalPresence: 'الحضور العالمي',
      globalText: 'تعمل SafePro عبر أوروبا والشرق الأوسط والأسواق الدولية، وتدعم العملاء طوال دورة حياة الأعمال والمشاريع.'
    },
    // Testimonials
    testimonials: {
      title: 'ماذا يقول عملاؤنا',
      subtitle: 'موثوق به من قبل قادة الصناعة في جميع أنحاء العالم'
    },
    // Contact
    contact: {
      title: 'اتصل بنا',
      subtitle: 'هل أنت مستعد لمناقشة متطلباتك؟',
      form: {
        name: 'الاسم الكامل',
        email: 'البريد الإلكتروني',
        phone: 'رقم الهاتف',
        company: 'اسم الشركة',
        service: 'الخدمة المطلوبة',
        message: 'رسالتك',
        submit: 'إرسال الرسالة',
        sending: 'جاري الإرسال...',
        success: 'شكراً لك! سنتواصل معك قريباً.',
        error: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
        selectService: 'اختر خدمة'
      },
      info: {
        location: 'الموقع',
        locationValue: 'الإمارات | نعمل عالمياً',
        email: 'البريد الإلكتروني',
        emailValue: 'info@safeproglobal.com',
        website: 'الموقع الإلكتروني',
        websiteValue: 'www.safeproglobal.com'
      }
    },
    // Footer
    footer: {
      description: 'تمكين الأعمال الآمنة والمتوافقة والذكية في جميع أنحاء العالم.',
      quickLinks: 'روابط سريعة',
      services: 'الخدمات',
      contact: 'معلومات الاتصال',
      rights: '© 2025 SafePro Global Consultancy. جميع الحقوق محفوظة.'
    },
    // CTA Section
    cta: {
      title: 'هل أنت مستعد لتحويل عملياتك؟',
      subtitle: 'دع خبراءنا يساعدونك في تحقيق الامتثال والسلامة والتميز التشغيلي.',
      button: 'ابدأ اليوم'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [dir, setDir] = useState('ltr');

  useEffect(() => {
    const savedLang = localStorage.getItem('safepro-language') || 'en';
    setLanguage(savedLang);
    setDir(savedLang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('dir', savedLang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', savedLang);
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'ar' : 'en';
    setLanguage(newLang);
    setDir(newLang === 'ar' ? 'rtl' : 'ltr');
    localStorage.setItem('safepro-language', newLang);
    document.documentElement.setAttribute('dir', newLang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', newLang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, dir, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
